(() => {
  const PHASE_STYLE = {
    normal: {
      line: '#EE005E',
      gradient: [
        { color: '#EE005E', opacity: 0.2 },
        { color: '#EE005E', opacity: 0 },
      ],
    },
    pregnancy: {
      line: '#D77091',
      gradient: [
        { color: '#D77091', opacity: 0.2 },
        { color: '#D77091', opacity: 0 },
      ],
    },
    labor: {
      line: '#D77091',
      gradient: [
        { color: '#D77091', opacity: 0.2 },
        { color: '#D77091', opacity: 0 },
      ],
    },
    cyclesReturn: {
      line: '#DC8EB6',
      gradient: [
        { color: '#DC8EB6', opacity: 0.2 },
        { color: '#DC8EB6', opacity: 0 },
      ],
    },
    perimenopause: {
      line: '#7F92CB',
      gradient: [
        { color: '#7083BE', opacity: 0.2 },
        { color: '#7284C4', opacity: 0 },
      ],
    },
    menopause: {
      line: '#717FA9',
      gradient: [
        { color: '#4A5585', opacity: 0.22 },
        { color: '#4A5585', opacity: 0 },
      ],
    },
  };

  const noise = (seed, amp = 0.045) => {
    const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
    return (x - Math.floor(x) - 0.5) * 2 * amp;
  };

  const mk = (key, label, sublabel, icon, start, end) => ({
    key,
    label,
    sublabel,
    icon,
    start,
    end,
    ...PHASE_STYLE[key],
  });

  const FOLL = 36.0;
  const NADIR = 35.82;
  const LUTEAL = 36.75;

  const pushOvulatory = (temps, day, len, { foll = FOLL, luteal = LUTEAL, lutealLen = 14, rocky = 0.045 } = {}) => {
    const ov = Math.max(8, len - lutealLen);
    for (let cd = 0; cd < len; cd++) {
      let base;
      if (cd < ov - 1) base = foll - 0.04 * (cd / Math.max(1, ov - 1));
      else if (cd === ov - 1 || cd === ov) base = NADIR + (foll - FOLL);
      else {
        const dpo = cd - ov;
        base = foll + (luteal - foll) * Math.min(dpo / 2, 1) + 0.02 * Math.min(dpo / 7, 1);
        if (cd >= len - 2) base = luteal - (luteal - (foll - 0.04)) * ((cd - (len - 2)) / 2);
      }
      temps.push(base + noise(day, rocky));
      day++;
    }
    return day;
  };

  const pushAnovulatory = (temps, day, len, mean = FOLL, rocky = 0.085) => {
    for (let cd = 0; cd < len; cd++) {
      const wander = 0.06 * Math.sin(cd * 0.7) + 0.05 * Math.sin(cd * 0.23);
      temps.push(mean + wander + noise(day, rocky));
      day++;
    }
    return day;
  };

  const pushPregnancy = (temps, day, total) => {
    const PEAK = 36.82;
    const PLATEAU = 36.76;
    const TROUGH = 36.5;
    const BIRTH_BUMP = 36.55;
    for (let cd = 0; cd < 13; cd++) {
      temps.push((cd < 11 ? FOLL - 0.05 * (cd / 11) : NADIR) + noise(day, 0.045));
      day++;
    }
    const M = total - 13;
    const peakAt = 22;
    const declStart = M - 26;
    const bumpStart = M - 5;
    for (let dpo = 0; dpo < M; dpo++) {
      let base;
      if (dpo <= 2) base = NADIR + (LUTEAL - NADIR) * (dpo / 2);
      else if (dpo <= peakAt) base = LUTEAL + (PEAK - LUTEAL) * ((dpo - 2) / (peakAt - 2));
      else if (dpo <= declStart) base = PEAK - (PEAK - PLATEAU) * ((dpo - peakAt) / (declStart - peakAt));
      else if (dpo < bumpStart) {
        const t = (dpo - declStart) / (bumpStart - declStart);
        base = PLATEAU - (PLATEAU - TROUGH) * (t * t);
      } else {
        const t = (dpo - bumpStart) / Math.max(1, M - 1 - bumpStart);
        base = TROUGH + (BIRTH_BUMP - TROUGH) * t;
      }
      temps.push(base + noise(day, 0.035));
      day++;
    }
    return day;
  };

  const pushLabor = (temps, day, days, top = 36.55, end = 36.05) => {
    for (let ld = 0; ld < days; ld++) {
      temps.push(top - ((top - end) * ld) / (days - 1) + noise(day, 0.05));
      day++;
    }
    return day;
  };

  const buildRegular = () => {
    const temps = [];
    const seg = [];
    const ann = [];
    let day = 0;
    let s;
    const A = (d, label, place, key, opts) => ann.push({ day: d, label, place, key, ...opts });

    s = day;
    for (let c = 0; c < 3; c++) day = pushOvulatory(temps, day, 28);
    A(s + 41, 'Ovulation', 'above', 'normal');
    A(s + 45, 'Thermal shift +0.4°C', 'below', 'normal', { ly: 175, labelDay: s + 52 });
    A(s + 26, 'Pre-period drop', 'above', 'normal');
    seg.push(
      mk(
        'normal',
        'Normal Cycles',
        'A clear temp rise of ~0.3-0.5°C after ovulation, then a dip at menstruation.',
        '✦',
        s,
        day - 1
      )
    );

    s = day;
    day = pushPregnancy(temps, day, 122);
    A(s + 12, 'Conception', 'below', 'pregnancy', { ly: 150 });
    A(s + 27, 'Period due: no drop', 'above', 'pregnancy');
    A(s + 104, '3rd-trimester decline', 'above', 'pregnancy');
    A(s + 119, 'Small rise into birth', 'below', 'pregnancy');
    seg.push(
      mk(
        'pregnancy',
        'Pregnancy',
        'Temp stays elevated during pregnancy, but eases in the third trimester before birth.',
        '◆',
        s,
        day - 1
      )
    );

    s = day;
    day = pushLabor(temps, day, 7);
    A(s + 1, 'Birth', 'above', 'labor', { fill: '#E4F5EB', textColor: '#1D284A', stroke: '#8FD0AD' });
    seg.push(
      mk(
        'labor',
        'Labor & Birth',
        'Progesterone withdrawal at delivery drops temperature back to baseline.',
        '✶',
        s,
        day - 1
      )
    );

    s = day;
    day = pushAnovulatory(temps, day, 40, 36.02, 0.075);
    A(s + 18, 'Anovulatory (breastfeeding)', 'above', 'cyclesReturn');
    A(s + 58, 'Ovulation returns', 'above', 'cyclesReturn');
    [32, 29, 28].forEach((len) => {
      day = pushOvulatory(temps, day, len);
    });
    seg.push(
      mk(
        'cyclesReturn',
        'Postpartum',
        'Cycles are often anovulatory at first while breastfeeding, ovulatory cycles will resume.',
        '✦',
        s,
        day - 1
      )
    );

    s = day;
    let pc = 0;
    const anovCycles = [];
    while (day - s < 104) {
      const anov = Math.abs(noise(pc * 13, 1)) < 0.35 + 0.3 * ((day - s) / 104);
      const len = 20 + Math.floor(Math.abs(noise(pc * 17, 1)) * 30);
      const cap = Math.min(len, s + 104 - day);
      if (anov) anovCycles.push({ mid: day + Math.floor(cap / 2), len: cap });
      day = anov ? pushAnovulatory(temps, day, cap, 36.3, 0.1) : pushOvulatory(temps, day, cap, { rocky: 0.07 });
      pc++;
    }
    A(s + 25, 'Erratic cycle lengths', 'above', 'perimenopause', { floating: true });
    if (anovCycles.length) {
      // Label the longest anovulatory cycle so it lands on unambiguously monophasic ground.
      const longest = anovCycles.reduce((a, b) => (b.len > a.len ? b : a));
      A(longest.mid, 'Anovulatory cycle', 'above', 'perimenopause');
    }
    seg.push(
      mk(
        'perimenopause',
        'Perimenopause',
        'Cycle lengths swing short to long; ovulatory and anovulatory cycles alternate.',
        '◇',
        s,
        day - 1
      )
    );

    s = day;
    for (let md = 0; md < 46; md++) {
      temps.push(36.0 + noise(day, 0.04));
      day++;
    }
    A(s + 22, 'Cycles have ceased', 'above', 'menopause', { floating: true });
    seg.push(
      mk(
        'menopause',
        'Menopause',
        'Cycles cease; with no progesterone cycling, temperature settles low and flat.',
        '○',
        s,
        day - 1
      )
    );

    return { temps, segments: seg, ann, total: temps.length };
  };

  const CHART_W = 960;
  const CHART_H = 360;
  const PAD = { top: 32, right: 28, bottom: 42, left: 56 };
  const PLOT_W = CHART_W - PAD.left - PAD.right;
  const PLOT_H = CHART_H - PAD.top - PAD.bottom;
  const T_MIN = 35.55;
  const T_MAX = 37.1;
  const WINDOW = 58;
  const PLAY_DURATION_MS = 56000;
  const PLAY_START_HOLD_MS = 1000;
  const PLAY_CYCLE_MS = PLAY_START_HOLD_MS + PLAY_DURATION_MS;
  const CURVE_TENSION = 0.62;

  const ANN_FONT_SIZE = 18;
  const ANN_PILL_H = 36;
  const ANN_DOT_R = 6;

  const tempToY = (t) => PAD.top + (1 - (t - T_MIN) / (T_MAX - T_MIN)) * PLOT_H;

  const buildSmoothPath = (points) => {
    if (points.length < 2) return '';
    const fmt = (n) => n.toFixed(1);
    let d = `M${fmt(points[0].x)},${fmt(points[0].y)}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[Math.max(i - 1, 0)];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[Math.min(i + 2, points.length - 1)];
      const cp1x = p1.x + ((p2.x - p0.x) / 6) * CURVE_TENSION;
      const cp1y = p1.y + ((p2.y - p0.y) / 6) * CURVE_TENSION;
      const cp2x = p2.x - ((p3.x - p1.x) / 6) * CURVE_TENSION;
      const cp2y = p2.y - ((p3.y - p1.y) / 6) * CURVE_TENSION;
      d += ` C${fmt(cp1x)},${fmt(cp1y)} ${fmt(cp2x)},${fmt(cp2y)} ${fmt(p2.x)},${fmt(p2.y)}`;
    }
    return d;
  };

  const hexToRgb = (hex) => {
    const n = parseInt(hex.replace('#', ''), 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  };

  const PHASE_ICON_SVG = `<svg viewBox="0 0 9.151 9.151" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2.79258 8.79106C2.23572 8.55076 1.75123 8.22471 1.3391 7.81292C0.926962 7.40112 0.600746 6.91714 0.360448 6.36096C0.120149 5.80478 0 5.21014 0 4.57702C0 3.94391 0.120149 3.3491 0.360448 2.79258C0.600407 2.23572 0.925945 1.75123 1.33706 1.3391C1.74818 0.926962 2.23234 0.600746 2.78953 0.360448C3.34672 0.120149 3.94154 0 4.57397 0C5.20641 0 5.80122 0.120149 6.35842 0.360448C6.91527 0.600407 7.39977 0.926115 7.8119 1.33757C8.22404 1.74903 8.55025 2.23318 8.79055 2.79004C9.03085 3.34689 9.151 3.94154 9.151 4.57397C9.151 5.20641 9.03102 5.80122 8.79106 6.35842C8.5511 6.91561 8.22505 7.40011 7.81292 7.8119C7.40078 8.2237 6.9168 8.54991 6.36096 8.79055C5.80512 9.03119 5.21048 9.15134 4.57702 9.151C3.94357 9.15066 3.34876 9.03068 2.79258 8.79106ZM7.4606 7.4606C8.24861 6.6726 8.64261 5.7109 8.64261 4.5755C8.64261 3.4401 8.24861 2.47839 7.4606 1.69039C6.6726 0.90239 5.7109 0.508389 4.5755 0.508389C3.4401 0.508389 2.47839 0.90239 1.69039 1.69039C0.90239 2.47839 0.508389 3.4401 0.508389 4.5755C0.508389 5.7109 0.90239 6.6726 1.69039 7.4606C2.47839 8.24861 3.4401 8.64261 4.5755 8.64261C5.7109 8.64261 6.6726 8.24861 7.4606 7.4606ZM2.77631 6.37672C2.28114 5.88291 2.03356 5.28335 2.03356 4.57804C2.03356 3.87274 2.28046 3.27199 2.77428 2.7758C3.26809 2.27962 3.86765 2.0322 4.57296 2.03356C5.27826 2.03491 5.87884 2.28182 6.37469 2.77428C6.87053 3.26674 7.11812 3.8663 7.11744 4.57296C7.11676 5.27962 6.86986 5.88019 6.37672 6.37469C5.88358 6.86918 5.28402 7.11676 4.57804 7.11744C3.87206 7.11812 3.27131 6.87121 2.7758 6.37672M6.01119 6.0117C6.40977 5.61346 6.60905 5.13473 6.60905 4.5755C6.60905 4.01627 6.40993 3.53754 6.0117 3.1393C5.61346 2.74106 5.13473 2.54194 4.5755 2.54194C4.01627 2.54194 3.53754 2.74106 3.1393 3.1393C2.74106 3.53754 2.54194 4.01627 2.54194 4.5755C2.54194 5.13473 2.74106 5.61346 3.1393 6.0117C3.53754 6.40993 4.01627 6.60905 4.5755 6.60905C5.13473 6.60905 5.61295 6.40993 6.01119 6.0117ZM4.21912 4.93188C4.11778 4.83054 4.06711 4.71175 4.06711 4.5755C4.06711 4.43925 4.11778 4.32046 4.21912 4.21912C4.32046 4.11778 4.43925 4.06711 4.5755 4.06711C4.71175 4.06711 4.83054 4.11778 4.93188 4.21912C5.03322 4.32046 5.08389 4.43925 5.08389 4.5755C5.08389 4.71175 5.03322 4.83054 4.93188 4.93188C4.83054 5.03322 4.71175 5.08389 4.5755 5.08389C4.43925 5.08389 4.32046 5.03322 4.21912 4.93188Z" fill="currentColor"/></svg>`;

  const PLAY_ICON_SVG = `<svg viewBox="0 0 10 12" fill="none" aria-hidden="true" focusable="false"><path d="M9.4 5.13a1 1 0 0 1 0 1.74l-7.9 4.56A1 1 0 0 1 0 10.56V1.44A1 1 0 0 1 1.5.57L9.4 5.13Z" fill="currentColor"/></svg>`;
  const PAUSE_ICON_SVG = `<svg viewBox="0 0 10 12" fill="none" aria-hidden="true" focusable="false"><rect x="0" y="0" width="3.4" height="12" rx="1.2" fill="currentColor"/><rect x="6.6" y="0" width="3.4" height="12" rx="1.2" fill="currentColor"/></svg>`;

  class TdCycleChartWidget extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.journey = buildRegular();
      this.leftEdge = 0;
      this.showAnn = true;
      this.dragging = false;
      this.playing = false;
      this.playElapsed = 0;
      this.playWallStart = null;
      this.animRef = null;
      this.hoveredPoint = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;

      this.maxLeft = this.journey.total - WINDOW;
      this.#buildDom();
      this.#bindEvents();
      this.#render();
    }

    #buildDom() {
      if (!this.dataset.widgetId) this.dataset.widgetId = String(Math.random()).slice(2, 8);
      const widgetId = this.dataset.widgetId;
      const escapeHtml = (value) => String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;');
      const phaseList = this.journey.segments
        .map((segment) => `<dt>${escapeHtml(segment.label)}</dt><dd>${escapeHtml(segment.sublabel)}</dd>`)
        .join('');

      this.innerHTML = `
        <div class="td-cycle-chart__widget-card">
          <div class="td-cycle-chart__widget-top">
            <div class="td-cycle-chart__widget-brand">
              <p class="td-cycle-chart__widget-eyebrow">TEMPDROP · LIFETIME VIEW</p>
              <h3 class="td-cycle-chart__widget-title">
                <span class="td-cycle-chart__widget-title-main">A woman's journey, told through </span>
                <span class="td-cycle-chart__widget-title-emphasis td-display">temperature data</span>
              </h3>
              <p class="td-cycle-chart__widget-subtitle">Your body's rhythm across cycles, pregnancy, postpartum, and beyond</p>
            </div>
            <button type="button" class="td-cycle-chart__ann-toggle" data-ann-toggle aria-pressed="true">Annotations on</button>
          </div>
          <div class="td-cycle-chart__phase-panel">
            <span class="td-cycle-chart__phase-icon" data-phase-icon aria-hidden="true"></span>
            <div class="td-cycle-chart__phase-copy">
              <p class="td-cycle-chart__phase-label" data-phase-label></p>
              <p class="td-cycle-chart__phase-sublabel" data-phase-sublabel></p>
            </div>
          </div>
          <div class="td-cycle-chart__chart-wrap">
            <h4 id="td-cc-title-${widgetId}" class="sr-only">Body temperature across one woman's reproductive lifetime</h4>
            <div id="td-cc-desc-${widgetId}" class="sr-only">
              <p>A continuous basal body temperature trace covering ${this.journey.total} days, in ${this.journey.segments.length} phases.</p>
              <dl>${phaseList}</dl>
            </div>
            <svg class="td-cycle-chart__svg" viewBox="0 0 ${CHART_W} ${CHART_H}" role="img" aria-labelledby="td-cc-title-${widgetId}" aria-describedby="td-cc-desc-${widgetId}">
              <defs>
                <clipPath id="td-cc-clip-${widgetId}">
                  <rect x="${PAD.left}" y="${PAD.top - 6}" width="${PLOT_W}" height="${PLOT_H + 6}"></rect>
                </clipPath>
                <linearGradient id="td-cc-grad-${widgetId}" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" data-grad-top></stop>
                  <stop offset="100%" data-grad-bottom></stop>
                </linearGradient>
              </defs>
              <g data-grid></g>
              <g clip-path="url(#td-cc-clip-${widgetId})">
                <path data-area d=""></path>
                <path data-line fill="none" stroke-width="3.6" stroke-linecap="round" stroke-linejoin="round"></path>
                <g data-dots></g>
              </g>
              <g data-annotations></g>
              <g data-tooltip hidden></g>
            </svg>
          </div>
          <div class="td-cycle-chart__scrubber-wrap">
            <div class="td-cycle-chart__scrubber" data-scrubber tabindex="0" role="slider" aria-label="Timeline position" aria-valuemin="0" aria-valuemax="100">
              <div class="td-cycle-chart__scrubber-segments" data-segments></div>
              <div class="td-cycle-chart__scrubber-lens" data-lens hidden></div>
            </div>
            <div class="td-cycle-chart__scrubber-labels" data-seg-labels></div>
          </div>
          <div class="td-cycle-chart__play-wrap">
            <button type="button" class="td-cycle-chart__play-btn" data-play-btn aria-pressed="false">
              <span class="td-cycle-chart__play-icon" data-play-icon>${PLAY_ICON_SVG}</span>
              <span data-play-label>Play the journey</span>
            </button>
          </div>
        </div>
      `;

      this.els = {
        annToggle: this.querySelector('[data-ann-toggle]'),
        phaseIcon: this.querySelector('[data-phase-icon]'),
        phaseLabel: this.querySelector('[data-phase-label]'),
        phaseSublabel: this.querySelector('[data-phase-sublabel]'),
        grid: this.querySelector('[data-grid]'),
        area: this.querySelector('[data-area]'),
        line: this.querySelector('[data-line]'),
        dots: this.querySelector('[data-dots]'),
        annotations: this.querySelector('[data-annotations]'),
        tooltip: this.querySelector('[data-tooltip]'),
        scrubber: this.querySelector('[data-scrubber]'),
        segments: this.querySelector('[data-segments]'),
        lens: this.querySelector('[data-lens]'),
        segLabels: this.querySelector('[data-seg-labels]'),
        playWrap: this.querySelector('.td-cycle-chart__play-wrap'),
        playBtn: this.querySelector('[data-play-btn]'),
        playIcon: this.querySelector('[data-play-icon]'),
        playLabel: this.querySelector('[data-play-label]'),
        gradTop: this.querySelector('[data-grad-top]'),
        gradBottom: this.querySelector('[data-grad-bottom]'),
      };

      if (this.reducedMotion) {
        this.els.lens.hidden = true;
        this.els.playWrap.hidden = true;
      }

      this.#buildGrid();
      this.#buildScrubber();
      this.els.phaseIcon.innerHTML = PHASE_ICON_SVG;
    }

    #buildGrid() {
      const ticks = [35.8, 36.2, 36.6, 37.0];
      this.els.grid.innerHTML = ticks
        .map(
          (t) => `
        <g>
          <line x1="${PAD.left}" x2="${CHART_W - PAD.right}" y1="${tempToY(t)}" y2="${tempToY(t)}" stroke="#E7E9F2" stroke-width="1" stroke-dasharray="${t === 36.2 ? 'none' : '4,4'}" opacity="${t === 36.2 ? 0.7 : 0.45}"></line>
          <text x="${PAD.left - 8}" y="${tempToY(t) + 4}" text-anchor="end" fill="#8d8d8d" font-size="13">${t.toFixed(1)}°</text>
        </g>`
        )
        .join('');
      this.els.grid.innerHTML += `<text x="15" y="${PAD.top + PLOT_H / 2}" transform="rotate(-90 15 ${PAD.top + PLOT_H / 2})" text-anchor="middle" fill="#8d8d8d" font-size="12.5" font-weight="500">Body temperature (°C)</text>`;
    }

    #buildScrubber() {
      const { segments, total } = this.journey;
      this.els.segments.innerHTML = segments
        .map((sg) => {
          const w = ((sg.end - sg.start + 1) / total) * 100;
          return `<span class="td-cycle-chart__scrubber-seg" data-seg-key="${sg.key}" style="width:${w}%;background:${sg.line};"></span>`;
        })
        .join('');

      this.els.segLabels.innerHTML = segments
        .map((sg) => {
          const w = ((sg.end - sg.start + 1) / total) * 100;
          const short = w < 9;
          return `<span class="td-cycle-chart__scrubber-label" data-seg-key="${sg.key}" style="width:${w}%;">${short ? '•' : sg.label}</span>`;
        })
        .join('');
    }

    #bindEvents() {
      this.els.annToggle.addEventListener('click', () => {
        this.showAnn = !this.showAnn;
        this.els.annToggle.setAttribute('aria-pressed', String(this.showAnn));
        this.els.annToggle.textContent = this.showAnn ? 'Annotations on' : 'Annotations off';
        this.#render();
      });

      this.els.scrubber.addEventListener('pointerdown', (e) => {
        this.#stopPlay();
        this.dragging = true;
        this.#setFromClientX(e.clientX);
        e.currentTarget.setPointerCapture(e.pointerId);
      });
      this.els.scrubber.addEventListener('pointermove', (e) => {
        if (this.dragging) this.#setFromClientX(e.clientX);
      });
      this.els.scrubber.addEventListener('pointerup', () => {
        this.dragging = false;
      });

      if (!this.reducedMotion) {
        this.els.playBtn.addEventListener('click', () => this.#togglePlay());
      }

      this.els.scrubber.addEventListener('keydown', (event) => {
        const step = this.journey.total * 0.02;
        const page = this.journey.total * 0.1;
        const moves = {
          ArrowRight: step,
          ArrowUp: step,
          ArrowLeft: -step,
          ArrowDown: -step,
          PageUp: page,
          PageDown: -page,
        };
        let next = null;

        if (Object.prototype.hasOwnProperty.call(moves, event.key)) {
          next = this.leftEdge + moves[event.key];
        } else if (event.key === 'Home') {
          next = 0;
        } else if (event.key === 'End') {
          next = this.maxLeft;
        } else {
          return;
        }

        event.preventDefault();
        this.#stopPlay();
        this.leftEdge = Math.max(0, Math.min(this.maxLeft, next));
        this.#syncPlayProgress();
        this.#render();
      });

      const svg = this.querySelector('.td-cycle-chart__svg');
      svg.addEventListener('pointermove', (e) => this.#handleSvgPointer(e, false));
      svg.addEventListener('pointerleave', () => {
        if (!this.isCoarsePointer) this.#clearTooltip();
      });
      svg.addEventListener(
        'click',
        (e) => {
          if (this.isCoarsePointer) this.#handleSvgPointer(e, true);
        },
        true
      );
    }

    #setFromClientX(clientX) {
      const r = this.els.scrubber.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
      this.leftEdge = Math.max(0, Math.min(this.maxLeft, pct * this.journey.total - WINDOW / 2));
      this.#syncPlayProgress();
      this.#render();
    }

    // Keep the tour clock in step with a manual scrub so Play resumes from where the
    // lens now sits instead of jumping back to where it was paused.
    #syncPlayProgress() {
      const progress = this.maxLeft > 0 ? this.leftEdge / this.maxLeft : 0;
      this.playElapsed = PLAY_START_HOLD_MS + progress * PLAY_DURATION_MS;
      this.playWallStart = null;
    }

    #handleSvgPointer(e, toggle) {
      const rect = e.currentTarget.getBoundingClientRect();
      const svgX = ((e.clientX - rect.left) / rect.width) * CHART_W;
      if (svgX < PAD.left || svgX > CHART_W - PAD.right) {
        if (!toggle) this.#clearTooltip();
        return;
      }
      const dayPct = (svgX - PAD.left) / PLOT_W;
      const currentDay = Math.round(this.leftEdge + dayPct * WINDOW);
      if (currentDay < 0 || currentDay >= this.journey.total) return;

      if (toggle && this.hoveredPoint?.day === currentDay) {
        this.#clearTooltip();
        return;
      }

      const x = PAD.left + ((currentDay - this.leftEdge) / WINDOW) * PLOT_W;
      const y = tempToY(this.journey.temps[currentDay]);
      this.hoveredPoint = { day: currentDay, x, y, temp: this.journey.temps[currentDay] };
      this.#renderTooltip();
    }

    #clearTooltip() {
      this.hoveredPoint = null;
      // `hidden` is an HTMLElement property; on an SVG <g> it must be set as an attribute.
      this.els.tooltip.setAttribute('hidden', '');
    }

    #activeSegment() {
      const center = this.leftEdge + WINDOW / 2;
      // Segments are contiguous on integer days but center is fractional, so a position
      // between two segments must resolve forward instead of falling back to the first one.
      const segments = this.journey.segments;
      return segments.find((sg) => center <= sg.end) || segments[segments.length - 1];
    }

    #render() {
      const { temps, ann, total } = this.journey;
      const active = this.#activeSegment();
      const startDay = Math.max(0, Math.floor(this.leftEdge) - 1);
      const endDay = Math.min(total - 1, Math.ceil(this.leftEdge + WINDOW) + 1);
      const xFor = (d) => PAD.left + ((d - this.leftEdge) / WINDOW) * PLOT_W;

      const points = [];
      const dots = [];
      for (let d = startDay; d <= endDay; d++) {
        const x = xFor(d);
        const y = tempToY(temps[d]);
        points.push({ x, y });
        if (d % 3 === 0 && x >= PAD.left - 4 && x <= CHART_W - PAD.right + 4) dots.push({ x, y });
      }

      const linePath = buildSmoothPath(points);
      const x0 = points[0]?.x ?? PAD.left;
      const x1 = points[points.length - 1]?.x ?? CHART_W - PAD.right;
      const baseline = PAD.top + PLOT_H;
      const areaPath = `${linePath} L${x1.toFixed(1)},${baseline.toFixed(1)} L${x0.toFixed(1)},${baseline.toFixed(1)} Z`;

      const gradTop = active.gradient[0];
      const gradBottom = active.gradient[1];
      const topRgb = hexToRgb(gradTop.color);
      const bottomRgb = hexToRgb(gradBottom.color);
      this.els.gradTop.setAttribute('stop-color', gradTop.color);
      this.els.gradTop.setAttribute('stop-opacity', String(gradTop.opacity));
      this.els.gradBottom.setAttribute('stop-color', gradBottom.color);
      this.els.gradBottom.setAttribute('stop-opacity', String(gradBottom.opacity));

      this.els.area.setAttribute('d', areaPath);
      this.els.area.setAttribute('fill', `url(#td-cc-grad-${this.dataset.widgetId})`);
      this.els.line.setAttribute('d', linePath);
      this.els.line.setAttribute('stroke', active.line);

      this.els.dots.innerHTML = dots.map((p) => `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="3.4" fill="${active.line}" opacity="0.35"></circle>`).join('');

      this.els.phaseLabel.textContent = active.label;
      this.els.phaseSublabel.textContent = active.sublabel;
      this.els.annToggle.style.borderColor = active.line;
      this.els.annToggle.style.color = this.showAnn ? active.line : '#8d8d8d';
      this.els.annToggle.style.background = this.showAnn ? `${active.line}1a` : 'transparent';

      this.#renderWeekTicks();
      this.#renderAnnotations(ann, active, xFor, temps);
      if (this.hoveredPoint) this.#renderTooltip(active);
      this.#renderScrubberState(active);
      this.#updatePlayButton(active);
    }

    disconnectedCallback() {
      this.#stopPlay();
    }

    #togglePlay() {
      if (this.playing) this.#stopPlay();
      else this.#startPlay();
    }

    #positionAt(elapsed) {
      const e = ((elapsed % PLAY_CYCLE_MS) + PLAY_CYCLE_MS) % PLAY_CYCLE_MS;
      if (e < PLAY_START_HOLD_MS) return 0;
      return ((e - PLAY_START_HOLD_MS) / PLAY_DURATION_MS) * this.maxLeft;
    }

    #startPlay() {
      this.playing = true;
      this.playWallStart = null;
      this.#clearTooltip();
      this.els.playBtn.setAttribute('aria-pressed', 'true');
      this.els.playIcon.innerHTML = PAUSE_ICON_SVG;
      this.els.playLabel.textContent = 'Pause';
      const step = (ts) => {
        if (!this.playing) return;
        if (this.playWallStart === null) this.playWallStart = ts - this.playElapsed;
        this.playElapsed = ts - this.playWallStart;
        if (this.playElapsed >= PLAY_CYCLE_MS) {
          this.playElapsed = 0;
          this.playWallStart = ts;
          this.leftEdge = 0;
        } else {
          this.leftEdge = this.#positionAt(this.playElapsed);
        }
        this.#render();
        this.animRef = requestAnimationFrame(step);
      };
      this.animRef = requestAnimationFrame(step);
    }

    #stopPlay() {
      if (!this.playing && !this.animRef) return;
      this.playing = false;
      this.playWallStart = null;
      if (this.animRef) {
        cancelAnimationFrame(this.animRef);
        this.animRef = null;
      }
      if (this.els?.playBtn) {
        this.els.playBtn.setAttribute('aria-pressed', 'false');
        this.els.playIcon.innerHTML = PLAY_ICON_SVG;
        this.els.playLabel.textContent = 'Play the journey';
        this.#updatePlayButton(this.#activeSegment());
      }
    }

    #updatePlayButton(active) {
      if (!this.els?.playBtn || this.reducedMotion) return;
      if (this.playing) {
        this.els.playBtn.style.background = active.line;
        this.els.playBtn.style.borderColor = active.line;
        this.els.playBtn.style.color = '#fff';
      } else {
        this.els.playBtn.style.background = 'transparent';
        this.els.playBtn.style.borderColor = '#cdd2e0';
        this.els.playBtn.style.color = '#7e859b';
      }
    }

    #renderWeekTicks() {
      const existing = this.querySelector('[data-week-ticks]');
      if (existing) existing.remove();
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('data-week-ticks', '');
      for (let d = Math.ceil(this.leftEdge / 7) * 7; d < this.leftEdge + WINDOW; d += 7) {
        const x = PAD.left + ((d - this.leftEdge) / WINDOW) * PLOT_W;
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', String(x));
        text.setAttribute('y', String(CHART_H - PAD.bottom + 16));
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('fill', '#8d8d8d');
        text.setAttribute('font-size', '14');
        text.textContent = `wk ${Math.round(d / 7)}`;
        g.appendChild(text);
      }
      this.querySelector('.td-cycle-chart__svg').appendChild(g);
    }

    #renderAnnotations(ann, active, xFor, temps) {
      if (!this.showAnn) {
        this.els.annotations.innerHTML = '';
        return;
      }
      const visible = ann
        .filter((a) => a.day >= this.leftEdge - 0.5 && a.day <= this.leftEdge + WINDOW + 0.5 && a.day < this.journey.total)
        .slice(0, 3);

      this.els.annotations.innerHTML = visible
        .map((a) => {
          const style = PHASE_STYLE[a.key] || active;
          const markerColor = a.stroke || style.line;
          const x = xFor(a.day);
          const py = tempToY(temps[a.day]);
          const ly = a.ly != null ? a.ly : a.place === 'below' ? 250 : 26;
          const w = a.label.length * ANN_FONT_SIZE * 0.56 + 30;
          const anchorX = a.labelDay != null ? xFor(a.labelDay) : x;
          const lx = Math.max(PAD.left + w / 2, Math.min(CHART_W - PAD.right - w / 2, anchorX));
          const leader = a.floating
            ? ''
            : `<line x1="${x}" y1="${py}" x2="${lx}" y2="${a.place === 'below' ? ly - ANN_PILL_H / 2 : ly + ANN_PILL_H / 2}" stroke="${markerColor}" stroke-width="1.5" stroke-dasharray="3,3" opacity="0.8"></line>
            <circle cx="${x}" cy="${py}" r="${ANN_DOT_R}" fill="#fff" stroke="${markerColor}" stroke-width="2.5"></circle>`;
          return `
          <g pointer-events="none">
            ${leader}
            <g transform="translate(${lx},${ly})">
              <rect x="${-w / 2}" y="${-ANN_PILL_H / 2}" width="${w}" height="${ANN_PILL_H}" rx="${ANN_PILL_H / 2}" fill="${a.fill || style.line}"></rect>
              <text x="0" y="${ANN_FONT_SIZE * 0.36}" text-anchor="middle" fill="${a.textColor || '#fff'}" font-size="${ANN_FONT_SIZE}" font-weight="600">${a.label}</text>
            </g>
          </g>`;
        })
        .join('');
    }

    #renderTooltip(active) {
      const p = this.hoveredPoint;
      if (!p) return;
      const color = (active || this.#activeSegment()).line;
      const boxW = 110;
      const boxH = 46;
      let bx = p.x + 12;
      let by = p.y - boxH / 2;
      if (bx + boxW > CHART_W - PAD.right) bx = p.x - boxW - 12;
      if (by < PAD.top) by = PAD.top;
      if (by + boxH > CHART_H - PAD.bottom) by = CHART_H - PAD.bottom - boxH;

      this.els.tooltip.removeAttribute('hidden');
      this.els.tooltip.innerHTML = `
        <line x1="${p.x}" y1="${PAD.top}" x2="${p.x}" y2="${CHART_H - PAD.bottom}" stroke="${color}" stroke-width="1.5" stroke-dasharray="3,3" opacity="0.6"></line>
        <circle cx="${p.x}" cy="${p.y}" r="6" fill="${color}" stroke="#fff" stroke-width="2"></circle>
        <g transform="translate(${bx}, ${by})">
          <rect width="${boxW}" height="${boxH}" rx="8" fill="#1D284A" opacity="0.95"></rect>
          <text x="8" y="18" fill="#8d8d8d" font-size="11" font-weight="500">Day ${p.day} (Wk ${Math.floor(p.day / 7)})</text>
          <text x="8" y="34" fill="#fff" font-size="14" font-weight="600">${p.temp.toFixed(2)}°C</text>
        </g>`;
    }

    #renderScrubberState(active) {
      const { total } = this.journey;
      const lensLeftPct = (this.leftEdge / total) * 100;
      const lensWidthPct = (WINDOW / total) * 100;
      if (!this.reducedMotion) {
        this.els.lens.hidden = false;
        this.els.lens.style.left = `${lensLeftPct}%`;
        this.els.lens.style.width = `${lensWidthPct}%`;
        this.els.lens.style.borderColor = active.line;
        this.els.lens.style.transition = this.dragging || this.playing ? 'none' : 'left 0.08s linear';
      }

      this.querySelectorAll('[data-seg-key]').forEach((el) => {
        const isActive = el.dataset.segKey === active.key;
        if (el.classList.contains('td-cycle-chart__scrubber-seg')) {
          el.style.opacity = isActive ? '1' : '0.33';
        } else {
          el.style.color = isActive ? active.line : '#8d8d8d';
          el.style.fontWeight = isActive ? '600' : '500';
        }
      });

      const centerDay = Math.round(this.leftEdge + WINDOW / 2);
      this.els.scrubber.setAttribute('aria-valuenow', String(Math.round(lensLeftPct)));
      this.els.scrubber.setAttribute('aria-valuetext', `Day ${centerDay} — ${active.label}`);
    }
  }

  if (!customElements.get('td-cycle-chart-widget')) {
    customElements.define('td-cycle-chart-widget', TdCycleChartWidget);
  }
})();
